import React, { useCallback, useEffect, useRef, useState } from "react";
import SideNavPOS from "../components/POS/SideNavPOS";
import POSOrderDetailTable from "../components/POS/POSOrderDetailTable";
import POSRightHeader from "../components/POS/POSRightHeader";
import POSCategory from "../components/POS/POSCategory";
import axios from "axios";
import { apiKey } from "../config/config";
import handleCSRF from "../config/CSRF";
import POSOrderDetail from "../components/POS/POSOrderDetail";
import InvoiceMain from "../components/Invoices/InvoiceMain";
import { useReactToPrint } from "react-to-print";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { RxCross1 } from "react-icons/rx";
import KOTSingle from "../components/Invoices/KOTSingle";
import { IoMdMenu } from "react-icons/io";
import { darkenHexColor, languageHelper } from "../config/helperFunctions";
import * as htmlToImage from "html-to-image";
import { toast } from "react-toastify";
import SubscriptionRenewalModal from "../components/POS/SubscriptionRenewalModal";

// ---------------------------------------------------------------------------
// CHANGES (bug: ZATCA QR code intermittently missing from printed receipts,
// worse/consistent on invoices with more line items):
//
// 1. waitForImagesToLoad() -- new helper, below.
// 2. captureStablePng()    -- new helper, below.
// 3. handleSilentPrint()   -- now calls both of the above before/instead of
//    the single htmlToImage.toPng() call it used to make. Search this file
//    for "captureStablePng(node" to find that call site.
// 4. The old `await new Promise((resolve) => setTimeout(resolve, 2000));`
//    that used to run at the top of handleSilentPrint has been removed --
//    it was a blind fixed delay hoping the QR would be ready by then, which
//    is exactly what these two helpers replace with something deterministic.
//
// Nothing else in this file was touched for this fix.
// ---------------------------------------------------------------------------

// Waits for every <img> inside a node (e.g. the ZATCA QR code) to actually
// finish loading AND decoding before it's safe to rasterize the node with
// html-to-image. A fixed setTimeout here is racy: the QR image sometimes
// hasn't finished painting yet, so the captured PNG silently misses it.
const waitForImagesToLoad = async (node) => {
  const images = Array.from(node.querySelectorAll("img"));

  await Promise.all(
    images.map((img) => {
      const decodeWhenReady = () =>
        img.decode ? img.decode().catch(() => {}) : Promise.resolve();

      if (img.complete && img.naturalWidth > 0) {
        return decodeWhenReady();
      }

      return new Promise((resolve) => {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      }).then(decodeWhenReady);
    })
  );

  // decode() confirms the image data is ready, but not that the browser has
  // already painted it into this frame -- give it two frames to be safe.
  await new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  );
};

// html-to-image doesn't screenshot the DOM directly: it clones the node,
// serializes it into an SVG, loads *that* as a fresh <img>, then rasterizes
// it to canvas. That internal load/render pass is itself async, and for
// larger/more complex nodes (e.g. an invoice with several line items) a
// single toPng() call can read the canvas out before that pass has actually
// finished -- silently dropping trailing content like the QR code.
//
// Rather than guess a fixed delay or a fixed number of "warm-up" calls (both
// of which are just smaller versions of the same race), capture repeatedly
// until two consecutive captures come back identical -- a reliable signal
// that rendering has stabilized -- up to a bounded number of attempts so
// this can never hang. If it never stabilizes (should be exceedingly rare),
// the last attempt is returned rather than blocking indefinitely; the caller
// still has react-to-print as an ultimate fallback if something throws.
const captureStablePng = async (node, options, maxAttempts = 3) => {
  let previous = null;
  let current = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    current = await htmlToImage.toPng(node, options);

    if (previous !== null && current.length === previous.length) {
      return current;
    }

    previous = current;

    // Let the browser flush any pending layout/paint work between attempts.
    await new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    );
  }

  console.warn(
    "Print capture did not stabilize after " +
      maxAttempts +
      " attempts; using the last one."
  );
  return current;
};

function POSMain({ outletData }) {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [orders, setOrders] = useState([]);
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [dineInOrders, setDineInOrders] = useState([]);
  const [takeawayOrders, setTakeawayOrders] = useState([]);
  const [holdOrders, setHoldORders] = useState([]);
  const [pendingRiderPayments, setPendingRiderPayments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [foodMenus, setFoodMenus] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [riders, setRiders] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [tables, setTables] = useState([]);
  const [isSubCat, setIsSubCat] = useState(false);
  const [selectOrder, setSelectOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderType, setSelectedOrderType] = useState(null);
  const [enabledOrderTypes, setEnabledOrderTypes] = useState([]);
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderItemDetail, setOrderItemDetail] = useState([]);
  const [totalItems, setTotalItems] = useState(null);
  const [totalVat, setTotalVat] = useState(null);
  const [subTotal, setSubTotal] = useState(null);
  const [subTotalWithVat, setSubTotalWithVat] = useState(null);
  const [totalDiscount, setTotalDiscount] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [discountVal, setDiscountVal] = useState(0);
  const [serviceDelivery, setServiceDelivery] = useState(null);
  const [allPaymentMethod, setAllPaymentMethod] = useState(null);
  const [totalPayable, setTotalPayable] = useState(0);
  const [originalTotalPayable, setOriginalTotalPayable] = useState(0);
  const [mainScreenDiscountType, setMainScreenDiscountType] = useState(null);
  const [mainScreenDiscount, setMainScreenDiscount] = useState(0);
  const [additionalDiscount, setAdditionalDiscount] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(1);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(1);
  const [selectedPaymentCompany, setSelectedPaymentCompany] = useState(null);
  const [multiplePayments, setMultiplePayments] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(false);
  const [walkInCustomer, setWalkInCustomer] = useState(null);
  const [setting, setSetting] = useState([]);
  const [currency, setCurrency] = useState("S.R")
  const [invoiceData, setInvoiceData] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [loyalty, setLoyalty] = useState(null);
  const [loyaltyPoints, setLoyaltyPoints] = useState("");
  const [cardDiscountType, setCardDiscountType] = useState("amount");
  const [cardDiscountAmount, setCardDiscountAmount] = useState(0);
  const [alert, setAlert] = useState(null);
  const [selectedCashier, setSelectedCashier] = useState("");
  const [selectedRider, setSelectedRider] = useState("");
  const [selectedWaiter, setSelectedWaiter] = useState("");
  const [selectedTable, setSelectedTable] = useState(null);
  const [showFinalizeOrderModal, setShowFinalizeOrderModal] = useState(false);
  const [showDirectInvoiceFinalizeModal, setShowDirectInvoiceFinalizeModal] =
    useState(false);
  const [posSetting, setPosSetting] = useState(0);
  const [showHoldButton, setShowHoldButton] = useState(false);
  const [payAmount, setPayAmount] = useState(0);
  const [dueAmount, setDueAmount] = useState(0);
  const [givenAmount, setGivenAmount] = useState(0);
  const [changeAmount, setChangeAmount] = useState(0);
  const [totalPayableFinal, setTotalPayableFinal] = useState(0);
  const [openKotModal, setOpenKotModal] = useState(false);
  const [printQueue, setPrintQueue] = useState([]);
  const [loading, setLoading] = useState(false);

  const printedRefs = useRef(new Map());

  // Subscription Renewal Modal state & 10-minute recurring interval logic
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [renewalSetupData, setRenewalSetupData] = useState(null);
  const lastShownRenewalTimeRef = useRef(null);

  const checkPosSetupStatus = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/customer/pos-setup-status`)
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          const setup = res.data.data;
          setRenewalSetupData(setup);

          const status = (setup.payment_status || "").toLowerCase();
          let isUnpaidOrExpiring = status === "unpaid";

          if (setup.renewal_date) {
            try {
              const renewalDate = new Date(setup.renewal_date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const diffTime = renewalDate.getTime() - today.getTime();
              const diffDays = Math.ceil(diffTime / (1000 * 3600 * 24));
              if (diffDays <= 14) {
                isUnpaidOrExpiring = true;
              }
            } catch (e) {
              console.error(e);
            }
          }

          if (isUnpaidOrExpiring) {
            const now = Date.now();
            // If never shown, or 10 minutes (600,000 ms) passed since last shown/dismissed
            if (
              !lastShownRenewalTimeRef.current ||
              now - lastShownRenewalTimeRef.current >= 600000
            ) {
              setShowRenewalModal(true);
              lastShownRenewalTimeRef.current = now;
            }
          }
        }
      })
      .catch((err) => console.log("POS setup status fetch error:", err));
  }, []);

  useEffect(() => {
    checkPosSetupStatus();

    // Check status and 10-minute recurring timer every 30 seconds
    const interval = setInterval(() => {
      checkPosSetupStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, [checkPosSetupStatus]);

  const handleCloseRenewalModal = () => {
    setShowRenewalModal(false);
    lastShownRenewalTimeRef.current = Date.now();
  };

  const handleCardDiscountType = (value) => {
    setCardDiscountType(value);
  };

  const handleCardDiscountAmount = (value) => {
    setCardDiscountAmount(value);
  };

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const clearQuery = () => {
    const params = new URLSearchParams(location.search);
    params.delete("editId");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true }
    );
  };

  const componentRef = useRef();

  // const printImageRef = useRef();
  // const handlePrint = useReactToPrint({
  //   content: () => componentRef.current,
  //   documentTitle: "Invoice",
  // });

  const resetPrintedRefs = () => {
    if (componentRef?.current) {
      componentRef.current.innerHTML = "";
    }
    printedRefs.current.clear();
  };

  const fallbackToReactPrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Invoice",
  });

  const handleCloseAlert = () => {
    setAlert(null);
  };

  const handleLoyalty = (points) => {
    setLoyaltyPoints(points);
  };
  const handleItemSelect = (item) => {
    const priceItem =
      selectedPaymentMethod === 6
        ? item.delivery_companies.find(
          (company) => company.delivery_company_id === selectedPaymentCompany
        ) || item
        : item;

    setOrderItemDetail((prev) => {
      return [
        ...prev,
        {
          food_menu_name: item.name,
          food_menu_name_ar: item.name_arabic,
          food_menu_id: item.id,
          food_menu_cooking_end_time: "",
          food_menu_cooking_start_time: "",
          food_menu_cooking_status: "",
          is_deal: item.is_deal,
          is_out_of_stock: item.out_of_stoct,
          deal_details: item.deal_details || null,
          food_menu_note: "",
          food_menu_qty: 1,
          food_menu_unit_price: Number(priceItem.sale_price || 0),
          food_menu_original_unit_price: Number(priceItem.sale_price || 0),
          food_menu_modifiers: item.modifiers,
          food_menu_vat: priceItem.sale_price_vat_price,
          food_menu_extra_vat: priceItem.sale_price_extra_vat_price,
          food_menu_discount:
            item.discount.length > 0
              ? item.discount[0]?.dis_type === "amount"
                ? item.discount[0]?.discount_amount
                : Number(priceItem.sale_price || 0) *
                (item.discount[0]?.discount_amount / 100)
              : 0,
          food_menu_total_sale_price:
            item.tax_method === "inclusive"
              ? Number(priceItem.sale_price || 0) -
              priceItem.sale_price_vat_price
              : Number(priceItem.sale_price || 0),
          food_menu_original_discount:
            item.discount.length > 0
              ? priceItem.discount[0]?.dis_type === "amount"
                ? item.discount[0]?.discount_amount
                : Number(priceItem.sale_price || 0) *
                (item.discount[0]?.discount_amount / 100)
              : 0,
          food_menu_after_discount: priceItem.sale_price_discount_price,
          food_menu_modifier_selected: [],
          is_weighted: item.category.is_weighted || 0,
          is_round_down: item.category.is_round_down || 0,
        },
      ];
    });
  };

  const handleCreateInvoice = () => {
    if (!selectOrder) {
      setAlert("Please select an order to proceed!");
      return;
    }
    setInvoiceData(null);
    resetPrintedRefs();
    const order = {
      customer_id: parseInt(selectOrder?.customer_id),
      redeem_point: selectOrder?.loyalty_point_amount || 0,
      is_guest: selectOrder?.is_guest,
      name:
        selectOrder?.is_guest === "yes" ? selectOrder?.guest?.name : undefined,
      session_id: "request",
      is_request: "pos",
      address: selectOrder?.address || "",
      alternate_phone: selectOrder?.alternate_phone || "",
      delivery_instructions: selectOrder?.delivery_instructions || "",
      nearest_landmark: selectOrder?.nearest_landmark || "",
      user_id: user.user.id,
      waiter_id: selectOrder?.waiter_id,
      cashier_id: selectOrder?.cashier_id,
      rider_id: selectOrder?.rider_id,
      order_from: selectOrder?.order_from,
      accepted_from: selectOrder?.accepted_from,
      order_type_id: selectOrder?.order_type_id,
      is_coupon: selectOrder?.is_coupon === "yes" ? "yes" : "no",
      coupon_code: selectOrder?.coupon_code,
      payment_method_id: selectOrder?.payment_method_id,
      payment_company_id: selectedPaymentCompany || null,
      card_discount_type: selectOrder?.card_discount_type,
      card_discount: selectOrder?.card_discount,
      multiple_payments: selectOrder?.multiple_payments,
      table_id: selectedTable,
      outlet_id: selectOrder?.outlet_id,
      sub_total: selectOrder?.sub_total,
      vat: totalVat,
      paid_amount: payAmount,
      received_amount: givenAmount || 0,
      change_amount: changeAmount,
      main_screen_discount_type: selectOrder?.main_screen_discount_type,
      main_screen_discount: selectOrder?.main_screen_discount,
      order_status: "invoiced",
      approval_status: "approved",
      delivery_charges: selectOrder?.delivery_charges,
      items: selectOrder?.order_details?.map((order) => ({
        item_id: order.food_menu_id,
        quantity: order.qty,
        menu_note: order.menu_note,
        variations: order.food_menu_deal_variations || [],
        modifiers: order.modifiers?.map((modifier) => {
          return {
            modifier_id: modifier.modifier_id || modifier.id,
            quantity: Number(Math.ceil(order.qty)),
          };
        }),
      })),
    };
    const addSale = async () => {
      try {
        const res = await axios.put(
          `${apiKey}/api/rms/admin/update_sale/${selectOrder?.id}`,
          order,
          {
            headers: {
              Authorization: `Bearer ${user?.token}`,
            },
          }
        );
        setInvoiceData(res.data.order_details?.original);
        setTimeout(() => {
          // handlePrint();
          setSelectOrder(null);
          setSelectedOrder(null);
          setOrderItemDetail([]);
          setSelectedCustomerId(1);
          setSelectedOrderType(enabledOrderTypes[0]?.ordertype_id || null);
          setMainScreenDiscount(0);
          setMainScreenDiscountType(null);
          setServiceDelivery(0);
          setSelectedPaymentMethod(defaultPaymentMethodId);
          setSelectedCashier("");
          setSelectedRider("");
          setSelectedWaiter("");
          setSelectedTable("");
          setServiceDelivery(0);
          setShowOrderDetailModal(false);
          setShowFinalizeOrderModal(false);
          setPayAmount(0);
          setDueAmount(0);
          setTotalPayableFinal(0);
          setGivenAmount(0);
          setChangeAmount(0);
          setCouponCode("");
          setSelectedCustomer(walkInCustomer?.customer);
        }, 100);
        fetchOrders();
        updateTables();
        updateFoodMenus();
        updateCustomers();
        clearQuery();
      } catch (err) {
        console.log(err);
      }
    };

    addSale();
  };

  const handleItemDelete = (index) => {
    setOrderItemDetail((prev) => {
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleOrderDetailModal = (open) => {
    if (!orderDetails) {
      return;
    }
    setShowOrderDetailModal(open);
  };

  const handleOrderDetails = (order) => {
    setOrderDetails(order);
  };

  const handleSelectOrder = (order) => {
    setSelectOrder(order);
  };

  const handleSelectOrderType = (orderType) => {
    setSelectedOrderType(orderType);
  };

  const handleSelectedPaymentMethod = (paymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
  };

  const handleIsSubCat = (val) => {
    setIsSubCat(val);
  };

  const handleDiscountUpdate = (amount) => {
    setAdditionalDiscount((prevTotalDiscount) => {
      let discountAmount = 0;
      let prevDiscountAmount = 0;

      // Calculate the previous discount value based on the previous discount type (percentage or fixed)
      if (typeof discount === "string" && discount.includes("%")) {
        const prevPercentage = parseFloat(discount.replace("%", ""));
        if (!isNaN(prevPercentage)) {
          prevDiscountAmount = (prevPercentage / 100) * subTotal;
        }
      } else {
        const prevParsedAmount = parseFloat(discount);
        if (!isNaN(prevParsedAmount)) {
          prevDiscountAmount = prevParsedAmount;
        }
      }

      // Calculate the new discount value based on the current amount
      if (typeof amount === "string" && amount.includes("%")) {
        const percentage = parseFloat(amount.replace("%", ""));
        setMainScreenDiscountType("percentage");

        // If the percentage is greater than 100, reset the discount
        if (isNaN(percentage) || percentage > 100) {
          setMainScreenDiscount(0);
          setDiscountVal(0);
          setDiscount("0%");
          return prevTotalDiscount - prevDiscountAmount;
        }

        if (!isNaN(percentage)) {
          setMainScreenDiscount(percentage);
          discountAmount = (percentage / 100) * subTotal;
        }
      } else {
        const parsedAmount = parseFloat(amount);
        setMainScreenDiscountType("amount");

        // If the discount amount exceeds the subTotal, reset the discount
        if (isNaN(parsedAmount) || parsedAmount > subTotal) {
          setMainScreenDiscount(0);
          setDiscountVal(0);
          setDiscount("0");
          return prevTotalDiscount - prevDiscountAmount;
        }

        if (!isNaN(parsedAmount)) {
          setMainScreenDiscount(parsedAmount);
          discountAmount = parsedAmount;
        }
      }

      setDiscountVal(discountAmount);

      // Calculate the new total discount by subtracting the previous discount and adding the new one
      return prevTotalDiscount - prevDiscountAmount + discountAmount;
    });

    // Store the new discount (as percentage or fixed)
    setDiscount(amount);
  };

  const handleDeliveryChargeUpdate = (amount) => {
    setServiceDelivery(amount);
  };

  const handleEditOrder = () => {
    setSelectedOrder(selectOrder);
    setSelectedRider(selectOrder?.delivery_rider?.rider_id);
    setSelectedWaiter(selectOrder?.waiter_id);
    setSelectedCashier(selectOrder?.cashier_id);
    setSelectedOrderType(selectOrder?.order_type_id);
    setSelectedTable(selectOrder?.table_id);
    setSelectedCustomerId(selectOrder?.customer_id);
    setCouponCode(selectOrder?.coupon_code || "");
    setOrderItemDetail(
      selectOrder?.order_details?.map((item) => ({
        food_menu_name: item.name,
        food_menu_id: item.food_menu_id,
        food_menu_cooking_end_time: item.cooking_end_time,
        food_menu_cooking_start_time: item.cooking_start_time,
        food_menu_cooking_status: item.cooking_status,
        is_deal: item.food_menu.is_deal,
        deal_details: item.food_menu.deal_details || null,
        food_menu_note: item.menu_note,
        food_menu_qty: item.qty,
        food_menu_unit_price: item.food_menu?.sale_price,
        food_menu_original_unit_price: item.food_menu?.sale_price,
        food_menu_modifiers: item.food_menu.modifiers,
        food_menu_vat: item.menu_taxes.vat,
        food_menu_extra_vat: item.menu_taxes.additional_vat,
        food_menu_discount: item.menu_taxes.single_discount,
        food_menu_original_discount: item.menu_taxes.single_discount,
        food_menu_total_sale_price: item.menu_taxes.item_price,
        food_menu_after_discount: item.menu_price_with_discount,
        food_menu_modifier_selected: item.modifiers.map((mod) => ({
          modifier_id: mod.id,
          modifier_name: mod.name,
          modifier_price: mod.price,
          quantity: mod.pivot.qty,
        })),
      }))
    );
    setIsUpdate(true);
  };

  const handleUpdateQuantity = (index, newQty) => {
    setOrderItemDetail((prevItems) => {
      const updatedItems = [...prevItems];
      const item = updatedItems[index];

      if (!item) return prevItems; // Edge case guard

      // Update values
      item.food_menu_qty = newQty;
      item.food_menu_unit_price = item.food_menu_original_unit_price * newQty;
      item.food_menu_discount = item.food_menu_original_discount * newQty;

      // // Remove item if quantity is 0
      // if (newQty === 0) {
      //   updatedItems.splice(index, 1);
      // }

      return updatedItems;
    });
  };

  const handleAddModifier = (index, modifiers, note, dealVariations) => {
    setOrderItemDetail((prevItems) => {
      return prevItems.map((item, idx) => {
        return idx === index
          ? {
            ...item,
            food_menu_modifier_selected: modifiers,
            food_menu_deal_variations: dealVariations,
            food_menu_note: note,
          }
          : item;
      });
    });
  };

  const sidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleCustomerSelect = (e) => {
    const selectedOption = e.target.options[e.target.selectedIndex]; // Access the selected <option> element
    setSelectedCustomerId(e.target.value); // Get the value of the selected option
    setSelectedCustomer(JSON.parse(selectedOption.dataset.customer)); // Access the data-customer attribute of the selected option
  };

  const handleMultiplePaymentSubmit = (payments) => {
    setMultiplePayments(payments);
  };

  const handleClearOrder = (skipConfirm = false) => {
    if (orderItemDetail.length > 0) {
      if (
        !skipConfirm &&
        window.confirm("Cart is not empty, want to clear the cart?") !== true
      ) {
        return;
      }
      setOrderItemDetail([]);
      setSelectedCustomerId(1);
      setSelectedOrder(null);
      setSelectOrder(null);
      setIsUpdate(false);
      setSelectedOrderType(null);
      setMainScreenDiscount(0);
      setMainScreenDiscountType(null);
      setServiceDelivery(0);
      setLoyaltyPoints("");
      setCouponCode("");
      setSelectedTable("");
      setSelectedCashier("");
      setSelectedRider("");
      setSelectedWaiter("");
      setGivenAmount(0);
      setPayAmount(0);
      setDueAmount(0);
      setTotalPayableFinal(0);
      setChangeAmount(0);
      setSelectedPaymentMethod(defaultPaymentMethodId);
    }
  };

  const handleHoldOrder = () => {
    if (orderItemDetail.length < 1) {
      setAlert("Cart is empty!");
      return;
    }
    if (!selectedOrderType) {
      setAlert("You must select Dine In or Take Away or Delivery!");
      return;
    }
    setLoading(true);
    if (selectedOrder) {
      axios
        .post(
          `${apiKey}/api/rms/admin/order/${selectedOrder?.id}/status`,
          {
            approval_status: "hold",
          },
          {
            headers: {
              Authorization: `Bearer ${user?.token}`,
            },
          }
        )
        .then((res) => {
          setOrderItemDetail([]);
          setSelectedCustomerId(1);
          setSelectedOrderType(null);
          setMainScreenDiscount(0);
          setMainScreenDiscountType(null);
          setIsUpdate(false);
          setServiceDelivery(0);
          setLoyaltyPoints("");
          setSelectedCashier("");
          setSelectedRider("");
          setSelectedWaiter("");
          fetchOrders();
          updateTables();
          updateFoodMenus();
          updateCustomers();
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => setLoading(false));
    } else {
      const order = {
        customer_id: parseInt(selectedCustomerId),
        redeem_point: loyaltyPoints,
        is_guest: selectedOrder?.is_guest === "yes" ? "yes" : "no",
        name:
          selectedOrder?.is_guest === "yes" ? selectOrder?.guest?.name : null,
        session_id: "request",
        is_request: "pos",
        address: selectedOrder?.address || "",
        alternate_phone: selectedOrder?.alternate_phone || "",
        delivery_instructions: selectedOrder?.delivery_instructions || "",
        nearest_landmark: selectedOrder?.nearest_landmark || "",
        user_id: user.user.id,
        waiter_id: selectedWaiter,
        cashier_id: selectedCashier,
        rider_id: selectedRider,
        order_from: selectedOrder?.order_from,
        accepted_from: selectedOrder?.accepted_from,
        order_type_id: selectedOrderType,
        is_coupon: couponCode ? "yes" : "no",
        coupon_code: couponCode,
        payment_method_id: selectedPaymentMethod,
        payment_company_id: selectedPaymentCompany || null,
        card_discount_type: cardDiscountType,
        card_discount: cardDiscountAmount,
        table_id: selectedTable,
        outlet_id: user.user.outlet_id,
        sub_total: subTotal,
        vat: totalVat,
        received_amount: givenAmount || 0,
        paid_amount: 0,
        main_screen_discount_type: mainScreenDiscountType,
        main_screen_discount: mainScreenDiscount,
        order_status: "new",
        approval_status: "hold",
        delivery_charges: serviceDelivery,
        items: orderItemDetail.map((order) => ({
          item_id: order.food_menu_id,
          quantity: order.food_menu_qty,
          menu_note: order.food_menu_note,
          variations: order.food_menu_deal_variations || [],
          modifiers: order.food_menu_modifier_selected?.map((modifier) => ({
            modifier_id: modifier.modifier_id,
            quantity: Number(Math.ceil(order.food_menu_qty)),
          })),
        })),
      };

      const addSale = async () => {
        try {
          // eslint-disable-next-line no-unused-vars
          const res = await axios.post(
            `${apiKey}/api/rms/customer/add_sale`,
            order,
            {
              headers: {
                Authorization: `Bearer ${user?.token}`,
              },
            }
          );
          setOrderItemDetail([]);
          setSelectedCustomerId(1);
          setSelectedOrderType(null);
          setMainScreenDiscount(0);
          setMainScreenDiscountType(null);
          setIsUpdate(false);
          setServiceDelivery(0);
          setLoyaltyPoints("");
          setSelectedCashier("");
          setSelectedRider("");
          setSelectedTable("");
          setSelectedWaiter("");
          setGivenAmount(0);
          fetchOrders();
          setCouponCode("");
          updateTables();
          updateFoodMenus();
          updateCustomers();
          setSelectedCustomer(walkInCustomer?.customer);
        } catch (err) {
          console.log(err);
        } finally {
          setLoading(false);
        }
      };

      addSale();
    }
  };

  const createUpdateOrder = (type) => {
    if (orderItemDetail.length < 1) {
      setAlert("Cart is empty!");
      return;
    }
    if (!selectedOrderType) {
      setAlert("You must select Dine In or Take Away or Delivery!");
      return;
    }

    setLoading(true);

    const order = {
      customer_id: parseInt(selectedCustomerId),
      redeem_point: loyaltyPoints,
      is_guest: selectedOrder?.is_guest === "yes" ? "yes" : "no",
      name: selectedOrder?.is_guest === "yes" ? selectOrder?.guest?.name : null,
      session_id: "request",
      is_request: "pos",
      address: selectedOrder?.address || "",
      alternate_phone: selectedOrder?.alternate_phone || "",
      delivery_instructions: selectedOrder?.delivery_instructions || "",
      nearest_landmark: selectedOrder?.nearest_landmark || "",
      user_id: user.user.id,
      waiter_id: selectedWaiter,
      cashier_id: selectedCashier,
      rider_id: selectedRider,
      order_from: selectedOrder?.order_from || "POS",
      accepted_from: selectedOrder?.accepted_from || "Web",
      order_type_id: selectedOrderType,
      is_coupon: couponCode ? "yes" : "no",
      coupon_code: couponCode,
      payment_method_id: selectedPaymentMethod,
      payment_company_id: selectedPaymentCompany || null,
      card_discount_type: cardDiscountType,
      card_discount: cardDiscountAmount,
      table_id: selectedTable,
      outlet_id: user.user.outlet_id,
      sub_total: subTotal,
      vat: totalVat,
      received_amount: givenAmount || 0,
      paid_amount: 0,
      main_screen_discount_type: mainScreenDiscountType,
      main_screen_discount: mainScreenDiscount,
      order_status: "new",
      approval_status: "approved",
      delivery_charges: serviceDelivery,
      items: orderItemDetail.map((order) => ({
        item_id: order.food_menu_id,
        quantity: order.food_menu_qty,
        menu_note: order.food_menu_note,
        variations: order.food_menu_deal_variations || [],
        modifiers: order.food_menu_modifier_selected?.map((modifier) => ({
          modifier_id: modifier.modifier_id,
          quantity: Number(Math.ceil(order.food_menu_qty)),
        })),
      })),
    };

    const addSale = async () => {
      if (type === "create") {
        try {
          // eslint-disable-next-line no-unused-vars
          const res = await axios.post(
            `${apiKey}/api/rms/customer/add_sale`,
            order,
            {
              headers: {
                Authorization: `Bearer ${user?.token}`,
              },
            }
          );
          // setOrders((prevOrders) => [res.data.order_details, ...prevOrders]);
          setAlert(
            "Order has been added to Running Orders and went to Kitchen Panel as well. Select any order from Running Orders to modify it or create invoice"
          );
          setOrderItemDetail([]);
          setSelectedCustomerId(1);
          setSelectedOrderType(null);
          setMainScreenDiscount(0);
          setMainScreenDiscountType(null);
          setIsUpdate(false);
          setServiceDelivery(0);
          setLoyaltyPoints("");
          setSelectedCashier("");
          setSelectedRider("");
          setSelectedWaiter("");
          setSelectedTable("");
          fetchOrders();
          updateTables();
          updateFoodMenus();
          updateCustomers();
          setSelectedOrder(null);
          setSelectOrder(null);
          setDiscount(0);
          setGivenAmount(0);
          setCouponCode("");
          setSelectedCustomer(walkInCustomer?.customer);
          setDiscountVal(0);
        } catch (err) {
          console.log(err);
        } finally {
          setLoading(false);
        }
      } else {
        try {
          // eslint-disable-next-line no-unused-vars
          const res = await axios.put(
            `${apiKey}/api/rms/admin/update_sale/${selectedOrder?.id}`,
            order,
            {
              headers: {
                Authorization: `Bearer ${user?.token}`,
              },
            }
          );
          // setOrders((prevOrders) => [res.data.order_details, ...prevOrders]);
          setAlert(
            "Order has been added to Running Orders and went to Kitchen Panel as well. Select any order from Running Orders to modify it or create invoice"
          );
          setOrderItemDetail([]);
          setSelectedCustomerId(1);
          setSelectedOrderType(null);
          setMainScreenDiscount(0);
          setMainScreenDiscountType(null);
          setIsUpdate(false);
          setServiceDelivery(0);
          fetchOrders();
          updateTables();
          updateFoodMenus();
          updateCustomers();
          setDiscount(0);
          setDiscountVal(0);
          setSelectedOrder(null);
          setGivenAmount(0);
          setSelectOrder(null);
          setCouponCode("");
          clearQuery();
          setSelectedCustomer(walkInCustomer?.customer);
        } catch (err) {
          console.log(err);
        } finally {
          setLoading(false);
        }
      }
    };

    addSale();
  };
  const handleDirectInvoice = () => {
    if (orderItemDetail.length < 1) {
      setAlert("Cart is empty!");
      return;
    }
    if (!selectedOrderType) {
      setAlert("You must select Dine In or Take Away or Delivery!");
      return;
    }
    setInvoiceData(null);
    resetPrintedRefs();
    if (
      selectedPaymentMethod === 1 &&
      JSON.parse(posSetting).received_amount === "yes"
    ) {
      setShowDirectInvoiceFinalizeModal(true);
      return;
    }

    setLoading(true);

    const order = {
      customer_id: parseInt(selectedCustomerId),
      redeem_point: loyaltyPoints,
      is_guest: selectedOrder?.is_guest === "yes" ? "yes" : "no",
      name: selectedOrder?.is_guest === "yes" ? selectOrder?.guest?.name : null,
      session_id: "request",
      is_request: "pos",
      address: selectedOrder?.address || "",
      alternate_phone: selectedOrder?.alternate_phone || "",
      delivery_instructions: selectedOrder?.delivery_instructions || "",
      nearest_landmark: selectedOrder?.nearest_landmark || "",
      user_id: user.user.id,
      waiter_id: selectedWaiter,
      cashier_id: selectedCashier,
      rider_id: selectedRider,
      order_from: selectedOrder?.order_from || "POS",
      accepted_from: selectedOrder?.accepted_from || "Web",
      order_type_id: selectedOrderType,
      is_coupon: couponCode ? "yes" : "no",
      coupon_code: couponCode,
      payment_method_id: selectedPaymentMethod,
      payment_company_id: selectedPaymentCompany || null,
      card_discount_type: cardDiscountType,
      card_discount: cardDiscountAmount,
      multiple_payments: multiplePayments,
      table_id: selectedTable,
      outlet_id: user.user.outlet_id,
      sub_total: subTotal,
      vat: totalVat,
      received_amount: givenAmount || 0,
      paid_amount: payAmount,
      change_amount: givenAmount - totalPayable,
      main_screen_discount_type: mainScreenDiscountType,
      main_screen_discount: mainScreenDiscount,
      order_status: "invoiced",
      approval_status: "approved",
      delivery_charges: serviceDelivery,
      items: orderItemDetail.map((order) => ({
        item_id: order.food_menu_id,
        quantity: order.food_menu_qty,
        menu_note: order.food_menu_note,
        variations: order.food_menu_deal_variations || [],
        modifiers: order.food_menu_modifier_selected?.map((modifier) => ({
          modifier_id: modifier.modifier_id,
          quantity: Number(Math.ceil(order.food_menu_qty)),
        })),
      })),
    };

    const addSale = async () => {
      if (selectedOrder) {
        try {
          const res = await axios.put(
            `${apiKey}/api/rms/admin/update_sale/${selectedOrder?.id}`,
            order,
            {
              headers: {
                Authorization: `Bearer ${user?.token}`,
              },
            }
          );
          setInvoiceData(res.data.order_details?.original);
          setTimeout(() => {
            // handlePrint();
            setOrderItemDetail([]);
            setSelectedCustomerId(1);
            setSelectedOrderType(enabledOrderTypes[0]?.ordertype_id || null);
            setMainScreenDiscount(0);
            setMainScreenDiscountType(null);
            setServiceDelivery(0);
            setSelectedPaymentMethod(defaultPaymentMethodId);
            setSelectedCashier("");
            setSelectedRider("");
            setSelectedWaiter("");
            setSelectedTable("");
            setCouponCode("");
            setSelectOrder(null);
            setServiceDelivery(0);
            setDiscount(0);
            setGivenAmount(0);
            setDiscountVal(0);
            setSelectedOrder(null);
            setShowDirectInvoiceFinalizeModal(false);
            setSelectedCustomer(walkInCustomer?.customer);
          }, 100);
          fetchOrders();
          updateTables();
          updateFoodMenus();
          updateCustomers();
          clearQuery();
        } catch (err) {
          console.log(err);
          err.response.data.message && setAlert(err.response.data.message);
        } finally {
          setLoading(false);
        }
      } else {
        try {
          const res = await axios.post(
            `${apiKey}/api/rms/customer/add_sale`,
            order,
            {
              headers: {
                Authorization: `Bearer ${user?.token}`,
              },
            }
          );
          setInvoiceData(res.data.order_details?.original);
          setTimeout(() => {
            // handlePrint();
            setOrderItemDetail([]);
            setSelectedCustomerId(1);
            setSelectedOrderType(enabledOrderTypes[0]?.ordertype_id || null);
            setMainScreenDiscount(0);
            setMainScreenDiscountType(null);
            setServiceDelivery(0);
            setSelectedPaymentMethod(defaultPaymentMethodId);
            setSelectedCashier("");
            setSelectedRider("");
            setSelectedWaiter("");
            setSelectOrder(null);
            setSelectedOrder(null);
            setServiceDelivery(0);
            setCouponCode("");
            setDiscount(0);
            setGivenAmount(0);
            setDiscountVal(0);
            setShowDirectInvoiceFinalizeModal(false);
          }, 100);
          fetchOrders();
          updateTables();
          updateFoodMenus();
          updateCustomers();
          setSelectedCustomer(walkInCustomer?.customer);
        } catch (err) {
          err.response.data.message && setAlert(err.response.data.message);
        } finally {
          setLoading(false);
        }
      }
    };

    // Call the function where necessary
    addSale();
  };

  const handleDirectCashInvoice = () => {
    setInvoiceData(null);
    resetPrintedRefs();
    setLoading(true);
    const order = {
      customer_id: parseInt(selectedCustomerId),
      redeem_point: loyaltyPoints,
      is_guest: selectedOrder?.is_guest === "yes" ? "yes" : "no",
      name: selectedOrder?.is_guest === "yes" ? selectOrder?.guest?.name : null,
      session_id: "request",
      is_request: "pos",
      address: selectedOrder?.address || "",
      alternate_phone: selectedOrder?.alternate_phone || "",
      delivery_instructions: selectedOrder?.delivery_instructions || "",
      nearest_landmark: selectedOrder?.nearest_landmark || "",
      is_coupon: couponCode ? "yes" : "no",
      coupon_code: couponCode,
      user_id: user.user.id,
      waiter_id: selectedOrder?.waiter_id,
      cashier_id: selectedCashier,
      rider_id: selectedOrder?.delivery_rider?.rider_id,
      order_from: selectedOrder?.order_from || "POS",
      accepted_from: selectedOrder?.accepted_from || "Web",
      order_type_id: selectedOrderType,
      payment_method_id: selectedPaymentMethod,
      payment_company_id: selectedPaymentCompany || null,
      card_discount_type: cardDiscountType,
      card_discount: cardDiscountAmount,
      multiple_payments: multiplePayments,
      table_id: selectedTable,
      outlet_id: user.user.outlet_id,
      sub_total: subTotal,
      vat: totalVat,
      received_amount: givenAmount || 0,
      paid_amount: payAmount,
      change_amount: givenAmount - totalPayable,
      main_screen_discount_type: mainScreenDiscountType,
      main_screen_discount: mainScreenDiscount,
      order_status: "invoiced",
      approval_status: "approved",
      delivery_charges: serviceDelivery,
      items: orderItemDetail.map((order) => ({
        item_id: order.food_menu_id,
        quantity: order.food_menu_qty,
        menu_note: order.food_menu_note,
        variations: order.food_menu_deal_variations || [],
        modifiers: order.food_menu_modifier_selected?.map((modifier) => ({
          modifier_id: modifier.modifier_id,
          quantity: Number(Math.ceil(order.food_menu_qty)),
        })),
      })),
    };

    const addSale = async () => {
      if (selectedOrder) {
        try {
          const res = await axios.put(
            `${apiKey}/api/rms/admin/update_sale/${selectedOrder?.id}`,
            order,
            {
              headers: {
                Authorization: `Bearer ${user?.token}`,
              },
            }
          );
          setInvoiceData(res.data.order_details?.original);
          setTimeout(() => {
            // handlePrint();
            setOrderItemDetail([]);
            setSelectedCustomerId(1);
            setSelectedOrderType(enabledOrderTypes[0]?.ordertype_id || null);
            setMainScreenDiscount(0);
            setMainScreenDiscountType(null);
            setServiceDelivery(0);
            setSelectedPaymentMethod(defaultPaymentMethodId);
            setSelectedCashier("");
            setSelectedRider("");
            setSelectedWaiter("");
            setSelectedTable("");
            setSelectOrder(null);
            setServiceDelivery(0);
            setDiscount(0);
            setGivenAmount(0);
            setDiscountVal(0);
            setCouponCode("");
            setSelectedOrder(null);
            setShowDirectInvoiceFinalizeModal(false);
            setSelectedCustomer(walkInCustomer?.customer);
          }, 100);
          fetchOrders();
          updateTables();
          updateFoodMenus();
          updateCustomers();
          clearQuery();
        } catch (err) {
          console.log(err);
        } finally {
          setLoading(false);
        }
      } else {
        try {
          const res = await axios.post(
            `${apiKey}/api/rms/customer/add_sale`,
            order,
            {
              headers: {
                Authorization: `Bearer ${user?.token}`,
              },
            }
          );
          setInvoiceData(res.data.order_details?.original);
          setTimeout(() => {
            // handlePrint();
            setOrderItemDetail([]);
            setSelectedCustomerId(1);
            setSelectedOrderType(enabledOrderTypes[0]?.ordertype_id || null);
            setMainScreenDiscount(0);
            setMainScreenDiscountType(null);
            setServiceDelivery(0);
            setSelectedPaymentMethod(defaultPaymentMethodId);
            setSelectedCashier("");
            setSelectedRider("");
            setSelectedWaiter("");
            setSelectOrder(null);
            setSelectedOrder(null);
            setServiceDelivery(0);
            setDiscount(0);
            setCouponCode("");
            setGivenAmount(0);
            setDiscountVal(0);
            setShowDirectInvoiceFinalizeModal(false);
            setSelectedCustomer(walkInCustomer?.customer);
          }, 100);
          fetchOrders();
          updateTables();
          updateFoodMenus();
          updateCustomers();
        } catch (err) {
          console.log(err);
        } finally {
          setLoading(false);
        }
      }
    };

    // Call the function where necessary
    addSale();
  };

  const handleStatusUpdate = (status, id) => {
    if (selectOrder) {
      axios
        .post(
          `${apiKey}/api/rms/admin/order/${id}/order_status`,
          {
            order_status: status,
          },
          {
            headers: {
              Authorization: `Bearer ${user?.token}`,
            },
          }
        )
        .then((res) => {
          // console.log(res.data);
          fetchOrders();
          updateTables();
          updateFoodMenus();
          updateCustomers();
          setOrderItemDetail([]);
          setSelectedCustomerId(1);
          setSelectedOrderType(enabledOrderTypes[0]?.ordertype_id || null);
          setMainScreenDiscount(0);
          setMainScreenDiscountType(null);
          setServiceDelivery(0);
          setSelectedPaymentMethod(defaultPaymentMethodId);
          setSelectedCashier("");
          setSelectedRider("");
          setSelectedWaiter("");
          setServiceDelivery(0);
        })
        .catch((err) => {
          setAlert(err?.response?.data?.message || "Failed to update order status!");
        });
    } else {
      setAlert("Please select an order to proceed!");
    }
  };

  const applyCoupon = () => {
    axios
      .post(`${apiKey}/api/rms/customer/check-coupons`, {
        code: couponCode,
        paid_amount: originalTotalPayable,
        customer_id: selectedCustomerId
      })
      .then((res) => {
        // setCouponDiscount(res.data.final_amount);
        // console.log(res.data);
        // setTotalPayableFinal(res.data.final_amount);
        setTotalPayable(res.data.final_amount);
        toast.success("Coupon applied successfully!");
      })
      .catch((err) => {
        // setCouponError(err.response.data.message);
        // setCouponCode("");
        setCouponCode("");
        setAlert(err?.response?.data?.message || "Failed to apply coupon!");
      });
  }

  const fetchCustomers = useCallback(() => {
    return axios.get(`${apiKey}/api/rms/admin/customer`, {
      headers: {
        Authorization: `Bearer ${user?.token}`,
      },
    });
  }, [user?.token]);

  const fetchTables = useCallback(() => {
    return axios.get(`${apiKey}/api/rms/admin/tables`, {
      headers: {
        Authorization: `Bearer ${user?.token}`,
      },
      params: {
        outletId: user?.user.outlet_id,
        available: true,
      },
    });
  }, [user?.token, user?.user.outlet_id]);

  const fetchFoodMenus = useCallback(() => {
    return axios.get(`${apiKey}/api/rms/admin/foodMenus`, {
      headers: {
        Authorization: `Bearer ${user?.token}`,
      },
      params: {
        catStatus: true,
        outletId: user?.user.outlet_id,
        status: "active",
        deliveryCompanies: 1,
      },
    });
  }, [user?.token, user?.user.outlet_id]);

  const updateFoodMenus = () => {
    fetchFoodMenus()
      .then((res) => {
        setFoodMenus(res.data.foodMenus);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const updateTables = () => {
    fetchTables()
      .then((res) => {
        const mappedTables = res.data.map((table) => ({
          label: table.name,
          value: table.id,
          is_available: table.is_available,
        }));
        setTables(mappedTables);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const updateCustomers = () => {
    fetchCustomers()
      .then((res) => {
        const mappedCustomers = res.data.map((customer) => ({
          label: `${customer.name} (${customer.phone})`,
          value: customer.id,
          customer: customer,
        }));
        setCustomers(mappedCustomers);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    const fetchCategories = () => {
      return axios.get(`${apiKey}/api/rms/admin/categories`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          status: "active",
          outletId: user?.user.outlet_id,
        },
      });
    };

    const fetchSubCategories = () => {
      return axios.get(`${apiKey}/api/rms/admin/subCategories`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          del_status: "Live",
          outletId: user?.user.outlet_id,
        },
      });
    };



    const fetchAllPayment = () => {
      const outletId = user?.user?.outlet_id;
      return axios.get(`${apiKey}/api/rms/admin/outlets/${outletId}/payment-methods`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          isActive: true
        }
      });
    };

    const fetchUsers = () => {
      return axios.get(`${apiKey}/api/rms/admin/user`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        params: {
          outletId: user?.user.outlet_id,
        },
      });
    };

    const fetchInvoiceSetting = () => {
      return axios.get(`${apiKey}/api/rms/admin/settings`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
    };

    const fetchLoyalty = () => {
      return axios.get(`${apiKey}/api/rms/admin/loyalty`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
    };

    const fetchOrderTypes = () => {
      return axios.get(`${apiKey}/api/rms/admin/ordertypes_outlets/${user?.user.outlet_id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
    };

    const fetchData = () => {
      Promise.all([
        fetchCategories(),
        fetchSubCategories(),
        fetchFoodMenus(),
        fetchAllPayment(),
        fetchCustomers(),
        fetchUsers(),
        fetchTables(),
        fetchInvoiceSetting(),
        fetchLoyalty(),
        fetchOrderTypes(),
      ])
        .then(
          ([
            catRes,
            subCatRes,
            foodMenuRes,
            allPaymentRes,
            customerRes,
            userRes,
            tableRes,
            settingRes,
            loyaltyRes,
            orderTypeRes,
          ]) => {
            setCategories(catRes.data);
            setSubCategories(subCatRes.data);
            setFoodMenus(foodMenuRes.data.foodMenus);
            setAllPaymentMethod(allPaymentRes.data);

            const activeMethods = allPaymentRes.data.filter((pm) => pm.status === "active");
            const defaultMethod = activeMethods.find((pm) => pm.is_default) || activeMethods[0];

            if (defaultMethod) {
              setSelectedPaymentMethod(defaultMethod.payment_method_id);
              setDefaultPaymentMethodId(defaultMethod.payment_method_id);
            }

            const activeOrderTypes = orderTypeRes.data.filter((ot) => ot.status === "active");
            setEnabledOrderTypes(activeOrderTypes);
            if (activeOrderTypes.length > 0) {
              setSelectedOrderType(activeOrderTypes[0].ordertype_id);
            }

            const mappedCustomers = customerRes.data.map((customer) => ({
              label: `${customer.name} (${customer.phone})`,
              value: customer.id,
              customer: customer,
            }));

            setCustomers(mappedCustomers);

            const walkIn = mappedCustomers.filter(
              (customer) => customer.value === 1
            )[0];

            setWalkInCustomer(walkIn);
            setSelectedCustomer(walkIn?.customer);
            setSelectedCustomerId(walkIn?.value);

            const waiters = userRes.data.filter(
              (user) => user.role === "waiter"
            );

            const mappedWaiters = waiters.map((waiter) => ({
              label: waiter.name,
              value: waiter.id,
            }));

            setWaiters(mappedWaiters);

            const riders = userRes.data.filter((user) => user.role === "rider");

            const mappedRiders = riders.map((rider) => ({
              label: rider.name,
              value: rider.id,
            }));

            setRiders(mappedRiders);

            const cashiers = userRes.data.filter(
              (user) => user.role === "cashier"
            );

            const mappedCashiers = cashiers.map((cashier) => ({
              label: cashier.name,
              value: cashier.id,
            }));

            setCashiers(mappedCashiers);

            const mappedTables = tableRes.data.map((table) => ({
              label: table.name,
              value: table.id,
              is_available: table.is_available,
            }));

            setTables(mappedTables);

            setSetting(settingRes.data);
            setCurrency(JSON.parse(settingRes.data.filter((set) => set.type === "currency")[0]?.value).value || "S.R");
            setPosSetting(
              settingRes.data.filter(
                (set) => set.type === "printer_settings"
              )[0]?.value
            );

            const generalSettingValue = settingRes.data.filter(
              (set) => set.type === "general"
            )[0]?.value;
            const generalSettings = generalSettingValue
              ? JSON.parse(generalSettingValue)
              : {};
            setShowHoldButton(generalSettings.show_hold_button === "yes");

            setLoyalty(loyaltyRes.data[0]);
          }
        )
        .catch((err) => {
          if (err.response.data.status_code === 401) {
            localStorage.removeItem("rms-admin");
            navigate("/admin");
          }
        })
        .finally(() => setPageLoading(false));
    };

    handleCSRF(fetchData);

    // handleCSRF(fetchOrders);
  }, [
    fetchCustomers,
    fetchTables,
    fetchFoodMenus,
    navigate,
    user?.token,
    user?.user.outlet_id,
  ]);

  const handleSilentPrint = useCallback(async () => {
    let parsedPosSetting;
    try {
      parsedPosSetting = JSON.parse(posSetting);
    } catch (e) {
      console.warn(
        "[JSPM] Printer settings not loaded yet (posSetting=" + posSetting + "), falling back to react-to-print"
      );
      fallbackToReactPrint();
      return;
    }
    console.log("[JSPM] jspm_print setting:", parsedPosSetting.jspm_print, "| websocket_status:", window.JSPM?.JSPrintManager?.websocket_status);
    if (parsedPosSetting.jspm_print === "yes") {
      try {
        // Ensure WebSocket is ready
        if (
          window.JSPM.JSPrintManager.websocket_status !==
          window.JSPM.WSStatus.Open
        ) {
          console.error("WebSocket not ready, falling back to react-to-print");
          fallbackToReactPrint();
          return;
        }

        const parentNode = componentRef.current;
        if (!parentNode) return;

        // Find all pages (invoice + KOT)
        const allPages = parentNode.querySelectorAll(".invoice-page");

        for (const node of allPages) {
          // Determine printer name (if any)
          const printerName = node.getAttribute("data-printer") || "Default";

          // Prevent re-printing the same printer job
          if (printedRefs.current.has(printerName)) continue;
          printedRefs.current.set(printerName, true);

          // Scale for better image capture
          node.style.width = 80 * 3.7795275591 + "px"; // 80mm → px

          // Wait for this page's images (e.g. the ZATCA QR code) to actually
          // finish loading/decoding before rasterizing -- fixes the QR
          // sometimes being missing from the printed receipt.
          await waitForImagesToLoad(node);

          const base64image = await captureStablePng(node, {
            cacheBust: true,
            pixelRatio: 2,
          });

          // Prepare client print job
          const cpj = new window.JSPM.ClientPrintJob();
          cpj.clientPrinter =
            printerName === "Default" ||
              printerName === "null" ||
              !printerName ||
              printerName === "Default Invoice"
              ? new window.JSPM.DefaultPrinter()
              : new window.JSPM.InstalledPrinter(printerName);

          cpj.files.push(
            new window.JSPM.PrintFile(
              base64image,
              window.JSPM.FileSourceType.Base64,
              "invoice.png",
              1
            )
          );

          // Send print job
          await cpj.sendToClient();

          // Optional: small delay between multiple printers (for safety)
          await new Promise((r) => setTimeout(r, 300));
        }
      } catch (error) {
        console.error("❌ Silent print error:", error);
        fallbackToReactPrint();
      }
    } else {
      fallbackToReactPrint();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posSetting]);

  useEffect(() => {
    if (printQueue.length === 0) return;
    const nextPrinter = printQueue[0];

    handleSilentPrint(nextPrinter).finally(() => {
      setPrintQueue((prev) => prev.slice(1)); // Remove the first printer
    });
  }, [handleSilentPrint, printQueue]);

  const fetchOrders = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/customer/fetch_all_order`, {
        headers: {
          Authorization: `Bearer ${user?.token}`
        },
        params: {
          outletId: user?.user.outlet_id,
          orderStatus: "new",
          noDecline: true,
        },
      })
      .then((res) => {
        setDineInOrders(
          res.data.filter(
            (item) =>
              item.approval_status === "pending" && item.order_type_id === 1
          )
        );
        setDeliveryOrders(
          res.data.filter(
            (item) =>
              item.approval_status === "pending" && item.order_type_id === 2
          )
        );
        setTakeawayOrders(
          res.data.filter(
            (item) =>
              item.approval_status === "pending" && item.order_type_id === 3
          )
        );
        setHoldORders(
          res.data.filter((item) => item.approval_status === "hold")
        );
        setOrders(
          res.data.filter((item) => item.approval_status === "approved")
        );
      })
      .catch((err) => {
        console.log(err);
      });
  }, [user?.user.outlet_id, user?.token]);

  // Delivery orders auto-close (order_status -> invoiced) the moment a
  // rider marks them delivered, so they drop out of fetch_all_order above
  // (that only returns order_status="new") before staff ever get a chance
  // to confirm the COD cash was handed back -- this is the "where does
  // this show in POS" gap. Pulled from rider-deliveries directly (not
  // orders) so it surfaces regardless of whether the order was placed via
  // POS or the customer/web app; both paths create the same
  // rider_deliveries row (see createRiderDelivery in MainScreenPOS.php).
  const fetchPendingRiderPayments = useCallback(() => {
    axios
      .get(`${apiKey}/api/rider-deliveries`, {
        headers: { Authorization: `Bearer ${user?.token}` },
      })
      .then((res) => {
        const delivered = (res.data.data || []).filter(
          (d) =>
            d.status === "delivered" &&
            d.payment_status !== "paid" &&
            d.outlet_id === user?.user.outlet_id
        );
        setPendingRiderPayments(delivered);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [user?.token, user?.user.outlet_id]);

  const confirmRiderPayment = (id) => {
    return axios
      .post(
        `${apiKey}/api/rider-deliveries/${id}/confirm-payment`,
        {},
        { headers: { Authorization: `Bearer ${user?.token}` } }
      )
      .then((res) => {
        fetchPendingRiderPayments();
        fetchOrders();
        return res;
      });
  };

  useEffect(() => {
    handleCSRF(fetchOrders);
    handleCSRF(fetchPendingRiderPayments);
  }, [fetchOrders, fetchPendingRiderPayments]);

  useEffect(() => {
    const interval = setInterval(() => {
      handleCSRF(fetchOrders);
      handleCSRF(fetchPendingRiderPayments);
    }, 10000); // 10 seconds in milliseconds

    // Cleanup function to clear the interval when the component unmounts
    return () => clearInterval(interval);
  }, [fetchOrders, fetchPendingRiderPayments]);

  // Extract the round_off setting
  const roundOff = setting.filter((set) => set.type === "invoice_settings")[0]
    ?.value
    ? JSON.parse(
      setting.filter((set) => set.type === "invoice_settings")[0].value
    ).round_off
    : "no";

  useEffect(() => {
    let total_discount = 0;
    let total_vat = 0;
    let sub_total = 0;
    let sub_total_with_vat = 0;

    orderItemDetail.forEach((item) => {
      const totalItemPrice =
        parseFloat(item.food_menu_total_sale_price) *
        parseFloat(item.food_menu_qty);
      sub_total += totalItemPrice;
      total_discount +=
        parseFloat(item.food_menu_original_discount) *
        parseFloat(item.food_menu_qty);
      let totalItemModPrice = 0;
      item.food_menu_modifier_selected?.forEach((modifier) => {
        totalItemModPrice +=
          parseFloat(modifier.modifier_price) *
          parseFloat(Math.ceil(item.food_menu_qty));
      });
      sub_total += totalItemModPrice;
      const totalItemVat =
        (item.food_menu_vat + item.food_menu_extra_vat) *
        parseFloat(item.food_menu_qty);
      total_vat += totalItemVat;

      let totalWithVat = totalItemModPrice + totalItemPrice + totalItemVat;

      if (Number(item.is_round_down) === 1 && roundOff === "only_cat") {
        totalWithVat = Math.floor(totalWithVat);
      }
      sub_total_with_vat += totalWithVat;
    });

    if (selectedPaymentMethod === 7) {
      if (cardDiscountType === "amount") {
        total_discount += parseFloat(cardDiscountAmount);
      } else {
        total_discount += sub_total * (parseFloat(cardDiscountAmount) / 100);
      }
    }

    if (selectedOrder?.is_coupon === "yes") {
      total_discount += parseFloat(selectedOrder?.coupon_discount_amount);
    }
    setTotalItems(orderItemDetail.length);
    setTotalDiscount((total_discount + discountVal).toFixed(2));
    setTotalVat(parseFloat(total_vat.toFixed(2)));
    setSubTotal(parseFloat(sub_total.toFixed(2)));
    setSubTotalWithVat(parseFloat(sub_total_with_vat.toFixed(2)));
  }, [
    cardDiscountAmount,
    cardDiscountType,
    discountVal,
    orderItemDetail,
    selectedOrder,
    selectedPaymentMethod,
    roundOff,
  ]);

  useEffect(() => {
    // Calculate the total payable amount
    let total = subTotalWithVat + (serviceDelivery || 0) - totalDiscount;

    // Round off the total if roundOff is set to "yes"
    const finalTotal =
      roundOff === "yes" ? Math.floor(total) : total.toFixed(2);

    setOriginalTotalPayable(finalTotal);
    setTotalPayable(finalTotal);
  }, [
    serviceDelivery,
    // subTotal,
    additionalDiscount,
    totalVat,
    totalDiscount,
    setting,
    roundOff,
    subTotalWithVat,
  ]);

  useEffect(() => {
    setDueAmount(totalPayableFinal - payAmount);
  }, [payAmount, totalPayableFinal]);

  useEffect(() => {
    setChangeAmount(givenAmount - payAmount);
  }, [givenAmount, payAmount]);

  useEffect(() => {
    axios
      .get(`${apiKey}/api/rms/admin/verifyShift`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => { })
      .catch((err) => {
        navigate("/admin/openRegister");
      });
  }, [navigate, user?.token]);

  useEffect(() => {
    if (window.JSPM && window.JSPM.JSPrintManager) {
      window.JSPM.JSPrintManager.auto_reconnect = true;
      window.JSPM.JSPrintManager.start();
      window.JSPM.JSPrintManager.WS.onStatusChanged = function () {
        const status = window.JSPM.JSPrintManager.websocket_status;
        if (status === window.JSPM.WSStatus.Open) {
          console.log("[JSPM] Connected to JSPrintManager Client (silent printing ready).");
        } else if (status === window.JSPM.WSStatus.Closed) {
          console.warn(
            "[JSPM] Not connected -- JSPrintManager Client app is not installed or not running on this PC. Silent printing will fall back to the browser print dialog."
          );
        } else if (status === window.JSPM.WSStatus.Blocked) {
          console.warn(
            "[JSPM] Connection BLOCKED -- this page is HTTPS and the browser blocked the insecure JSPM connection (mixed content / SSL). " +
            "The JSPrintManager Client app needs its local SSL certificate installed/trusted for this browser to allow silent printing over HTTPS. " +
            "Silent printing will fall back to the browser print dialog."
          );
        }
      };
    } else {
      console.error("JSPrintManager not loaded.");
    }
  }, []);

  useEffect(() => {
    axios.get(`${apiKey}/api/rms/customer/settings`).then((res) => {
      let colors = res.data.filter((item) => item.type === "color_theme");
      colors = JSON.parse(colors[0].value);

      document.documentElement.style.setProperty(
        "--color-d-bg",
        colors.color_1
      );
      document.documentElement.style.setProperty(
        "--color-d-pop-btn",
        colors.color_2
      );
      document.documentElement.style.setProperty(
        "--color-d-bg-text",
        colors.color_3
      );
      document.documentElement.style.setProperty(
        "--color-d-btn-text",
        colors.color_4
      );
      document.documentElement.style.setProperty(
        "--color-d-cat-btn",
        colors.color_5
      );

      document.documentElement.style.setProperty(
        "--color-d-bg-dark",
        darkenHexColor(colors.color_5, 30)
      );
    });
  }, []);

  // If the URL has ?editId=123, fetch that order and prefill edit mode
  useEffect(() => {
    const id = searchParams.get("editId");
    if (!id) return;

    axios
      .get(`${apiKey}/api/rms/customer/fetch_order/${id}`, {
        params: {
          orderId: id,
          outletId: user?.user.outlet_id,
        },
      })
      .then((res) => {
        // API may return the object directly or under .order
        const o = res.data?.order || res.data;
        if (!o) return;

        // Mirror existing manual “Edit” behavior (don’t change any working logic)
        setSelectOrder(o);
        setSelectedOrder(o);
        setSelectedOrderType(o?.order_type_id ?? null);
        setSelectedTable(o?.table_id ?? "");
        setSelectedCustomerId(o?.customer_id ?? 1);

        setOrderItemDetail(
          o?.order_details?.map((item) => ({
            food_menu_name: item.name,
            food_menu_name_ar: item.name_arabic, // if present
            food_menu_id: item.food_menu_id,
            food_menu_cooking_end_time: item.cooking_end_time,
            food_menu_cooking_start_time: item.cooking_start_time,
            food_menu_cooking_status: item.cooking_status,
            is_deal: item.food_menu?.is_deal,
            deal_details: item.food_menu?.deal_details || null,
            food_menu_note: item.menu_note,
            food_menu_qty: item.qty,
            food_menu_unit_price: item.food_menu?.sale_price,
            food_menu_original_unit_price: item.food_menu?.sale_price,
            food_menu_modifiers: item.food_menu?.modifiers,
            food_menu_vat: item.menu_taxes?.vat ?? 0,
            food_menu_extra_vat: item.menu_taxes?.additional_vat ?? 0,
            food_menu_discount: item.menu_taxes?.single_discount ?? 0,
            food_menu_original_discount: item.menu_taxes?.single_discount ?? 0,
            food_menu_total_sale_price: item.menu_taxes?.item_price ?? 0,
            food_menu_after_discount: item.menu_price_with_discount ?? 0,
            is_weighted: item.food_menu.category?.is_weighted || 0,
            is_round_down: item.food_menu.category?.is_round_down || 0,
            food_menu_modifier_selected:
              item.modifiers?.map((mod) => ({
                modifier_id: mod.id,
                modifier_name: mod.name,
                modifier_price: mod.price,
                quantity: mod.pivot?.qty ?? item.qty,
              })) || [],
          })) || []
        );

        // Flip UI into “Update” mode so your existing Update button/flow is used
        setIsUpdate(true);
      })
      .catch((err) => {
        console.log(err);
        // Optional: surface a friendly alert if needed
        setAlert("Unable to load order for editing.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user?.user.outlet_id]);

  if (pageLoading) {
    return (
      <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="" />
      </div>
    );
  }

  return (
    <>
      <div className="bg-[#f0f5f9] flex gap-x-4 w-full relative">
        <div
          className={`max-w-[185px] transition-all ${sidebarOpen ? "lg:w-full w-0" : "lg:w-0 w-0 fixed z-10"
            }`}
        >
          <button
            className="lg:hidden p-2 rounded-r-md z-10 bg-d-cat-btn text-white fixed top-16 left-0"
            onClick={sidebarToggle}
          >
            <IoMdMenu className="text-2xl" />
          </button>
          <div
            onClick={sidebarToggle}
            className={`w-screen h-screen bg-black backdrop-blur-md bg-opacity-50 fixed top-0 left-0 z-10 lg:hidden ${sidebarOpen ? "hidden" : ""
              }`}
          ></div>
          <div
            className={`max-w-[185px] max-h-screen fixed top-0 left-0 bottom-0 transition-all z-10 ${sidebarOpen
              ? "-translate-x-full w-0 lg:translate-x-0 lg:w-full"
              : "lg:-translate-x-full lg:w-0 translate-x-0 w-full"
              } overflow-x-clip`}
          >
            <SideNavPOS
              orders={orders}
              handleSelectOrder={handleSelectOrder}
              selectOrder={selectOrder}
              handleEditOrder={handleEditOrder}
              handleOrderDetailModal={handleOrderDetailModal}
              handleOrderDetails={handleOrderDetails}
              handleStatusUpdate={handleStatusUpdate}
              fetchOrders={fetchOrders}
              handleCreateInvoice={handleCreateInvoice}
              setShowFinalizeOrderModal={setShowFinalizeOrderModal}
              setPayAmount={setPayAmount}
              setTotalPayableFinal={setTotalPayableFinal}
              setOpenKotModal={setOpenKotModal}
            />
          </div>
        </div>
        <div className="w-full flex transition-all">
          <div className="w-full px-2">
            <POSOrderDetailTable
              user={user}
              currency={currency}
              sidebarToggle={sidebarToggle}
              sidebarOpen={sidebarOpen}
              orderItemDetail={orderItemDetail}
              deliveryOrders={deliveryOrders}
              dineInOrders={dineInOrders}
              takeawayOrders={takeawayOrders}
              holdOrders={holdOrders}
              pendingRiderPayments={pendingRiderPayments}
              confirmRiderPayment={confirmRiderPayment}
              allPaymentMethod={allPaymentMethod}
              handleMultiplePaymentSubmit={handleMultiplePaymentSubmit}
              handleItemDelete={handleItemDelete}
              selectedOrderType={selectedOrderType}
              enabledOrderTypes={enabledOrderTypes}
              handleSelectOrderType={handleSelectOrderType}
              handleDiscountUpdate={handleDiscountUpdate}
              handleDeliveryChargeUpdate={handleDeliveryChargeUpdate}
              handleUpdateQuantity={handleUpdateQuantity}
              handleAddModifier={handleAddModifier}
              setSelectedPaymentCompany={setSelectedPaymentCompany}
              selectedPaymentCompany={selectedPaymentCompany}
              customers={customers}
              waiters={waiters}
              riders={riders}
              cashiers={cashiers}
              tables={tables}
              selectedCustomerId={selectedCustomerId}
              selectedCustomer={selectedCustomer}
              setSelectedCustomer={setSelectedCustomer}
              setAlert={setAlert}
              loyaltyPoint={loyaltyPoints}
              handleLoyalty={handleLoyalty}
              handleCustomerSelect={handleCustomerSelect}
              totalItems={totalItems}
              totalVat={totalVat}
              subTotal={subTotal}
              totalDiscount={totalDiscount}
              discount={discount}
              serviceDelivery={serviceDelivery}
              totalPayable={totalPayable}
              originalTotalPayable={originalTotalPayable}
              selectedPaymentMethod={selectedPaymentMethod}
              handleSelectedPaymentMethod={handleSelectedPaymentMethod}
              handleDirectInvoice={handleDirectInvoice}
              isUpdate={isUpdate}
              createUpdateOrder={createUpdateOrder}
              handleClearOrder={handleClearOrder}
              loyalty={loyalty}
              handleCardDiscountType={handleCardDiscountType}
              handleCardDiscountAmount={handleCardDiscountAmount}
              fetchOrders={fetchOrders}
              updateTables={updateTables}
              selectedCashier={selectedCashier}
              setSelectedCashier={setSelectedCashier}
              selectedRider={selectedRider}
              setSelectedRider={setSelectedRider}
              selectedWaiter={selectedWaiter}
              setSelectedWaiter={setSelectedWaiter}
              selectedTable={selectedTable}
              setSelectedTable={setSelectedTable}
              handleHoldOrder={handleHoldOrder}
              showHoldButton={showHoldButton}
              setSelectedOrder={setSelectedOrder}
              setSelectedOrderType={setSelectedOrderType}
              setOrderItemDetail={setOrderItemDetail}
              setIsUpdate={setIsUpdate}
              fetchCustomers={fetchCustomers}
              setCustomers={setCustomers}
              setSelectedCustomerId={setSelectedCustomerId}
              loading={loading}
              couponCode={couponCode}
              setCouponCode={setCouponCode}
              applyCoupon={applyCoupon}
            />
          </div>
          <div
            className={`w-screen h-screen bg-black backdrop-blur-md bg-opacity-50 fixed top-0 left-0 z-10 lg:hidden ${!menuOpen && "hidden"
              }`}
            onClick={() => setMenuOpen(false)}
          ></div>
          <div
            className={`lg:w-full w-[75%] bg-white px-2 fixed lg:static top-0 right-0 lg:transalte-x-0 ${!menuOpen
              ? "translate-x-full lg:translate-x-0 min-h-screen"
              : "translate-x-0"
              } z-50 lg:z-0 transition-all`}
          >
            <button
              className="lg:hidden absolute top-10 left-0 -translate-x-full bg-d-cat-btn text-white text-lg font-medium px-4 py-1 rounded-tl rounded-bl"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? "Close" : "Menu"}
            </button>
            <POSRightHeader />
            <POSCategory
              categories={categories}
              subCategories={subCategories}
              foodMenus={foodMenus}
              handleItemSelect={handleItemSelect}
              handleIsSubCat={handleIsSubCat}
              isSubCat={isSubCat}
              selectedPaymentCompany={selectedPaymentCompany}
              selectedPaymentMethod={selectedPaymentMethod}
              currency={currency}
            />
          </div>
        </div>
      </div>
      {showOrderDetailModal && (
        <POSOrderDetail
          toggle={handleOrderDetailModal}
          orderDetails={orderDetails}
          handleCreateInvoice={handleCreateInvoice}
          setShowFinalizeOrderModal={setShowFinalizeOrderModal}
          setPayAmount={setPayAmount}
          setTotalPayableFinal={setTotalPayableFinal}
          currency={currency}
        />
      )}
      {showFinalizeOrderModal && (
        <div
          className="fixed z-10 top-0 left-0 w-full bg-black bg-opacity-50 min-h-screen grid place-items-center"
          onClick={() => {
            setPayAmount(0);
            setDueAmount(0);
            setTotalPayableFinal(0);
            setGivenAmount(0);
            setChangeAmount(0);
            setShowFinalizeOrderModal(false);
          }}
        >
          <div
            className="max-w-lg w-full bg-white rounded-lg overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-d-bg text-white flex justify-between items-center px-4 py-3">
              <h2 className="text-lg font-semibold">
                {languageHelper("Finalize Order")}
              </h2>
              <RxCross1
                className="cursor-pointer hover:text-gray-200"
                onClick={() => {
                  setPayAmount(0);
                  setDueAmount(0);
                  setTotalPayableFinal(0);
                  setGivenAmount(0);
                  setChangeAmount(0);
                  setShowFinalizeOrderModal(false);
                }}
              />
            </div>
            <div className="p-4 space-y-4">
              <div className="flex justify-between items-center border-b pb-2">
                <span className="font-medium">
                  {languageHelper("Total Payable")}
                </span>
                <span className="font-semibold text-gray-800">
                  {currency || "S.R"} {totalPayableFinal}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <label className="w-full">
                  {languageHelper("Payment Method")}
                </label>
                <select
                  className="border border-gray-300 rounded p-2 w-full"
                  onChange={(e) => setSelectedPaymentMethod(parseInt(e.target.value) || "")}
                  value={selectedPaymentMethod}
                >
                  <option value="">{languageHelper("Select Method")}</option>
                  {allPaymentMethod &&
                    allPaymentMethod
                      .filter((pm) => pm.status === "active")
                      .map((pm) => (
                        // `pm.id` is the outlet-assignment row's own id --
                        // the real payment method id (matching what the
                        // default-selection logic above already uses) is
                        // pm.payment_method_id, and the name is nested
                        // under pm.payment_method, not on pm directly.
                        <option key={pm.id} value={pm.payment_method_id}>
                          {languageHelper(pm.payment_method?.name)}
                        </option>
                      ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1">
                    {languageHelper("Pay Amount")}
                  </label>
                  <input
                    type="text"
                    className="border border-gray-300 rounded p-2 w-full"
                    onChange={(e) =>
                      setPayAmount(parseFloat(e.target.value) || 0)
                    }
                    value={payAmount}
                  />
                </div>
                <div>
                  <label className="block mb-1">
                    {languageHelper("Due Amount")}
                  </label>
                  <input
                    type="text"
                    className="border border-gray-300 rounded p-2 w-full"
                    onChange={(e) => setDueAmount(parseFloat(e.target.value))}
                    value={dueAmount}
                  />
                </div>
                <div>
                  <label className="block mb-1">
                    {languageHelper("Given Amount")}
                  </label>
                  <input
                    type="text"
                    className="border border-gray-300 rounded p-2 w-full"
                    onChange={(e) => setGivenAmount(e.target.value || 0)}
                    value={givenAmount}
                  />
                </div>
                <div>
                  <label className="block mb-1">
                    {languageHelper("Change Amount")}
                  </label>
                  <input
                    type="text"
                    className="border border-gray-300 rounded p-2 w-full"
                    onChange={(e) => setChangeAmount(e.target.value)}
                    value={changeAmount}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-4 mt-4">
                <button
                  onClick={() => {
                    setPayAmount(0);
                    setDueAmount(0);
                    setTotalPayableFinal(0);
                    setGivenAmount(0);
                    setChangeAmount(0);
                    setShowFinalizeOrderModal(false);
                  }}
                  className="text-white bg-d-cat-btn px-4 py-2 rounded-md"
                >
                  {languageHelper("Cancel")}
                </button>
                <button
                  onClick={() => handleCreateInvoice()}
                  className="text-white bg-d-cat-btn px-4 py-2 rounded-md"
                >
                  {languageHelper("Submit")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDirectInvoiceFinalizeModal && (
        <div
          className="fixed z-10 top-0 left-0 w-full bg-black bg-opacity-50 min-h-screen py-16"
          onClick={() => setShowDirectInvoiceFinalizeModal(false)}
        >
          <div
            className="max-w-lg w-full bg-white rounded-lg overflow-hidden relative mx-auto p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h1 className="text-2xl font-semibold text-center">
              {languageHelper("Received Amount")}
            </h1>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (givenAmount < totalPayable) return;
                handleDirectCashInvoice();
              }}
            >
              <div>
                <label className="block mb-1">
                  {languageHelper("Given Amount")}
                </label>
                <input
                  type="text"
                  className="border border-gray-300 rounded p-2 w-full"
                  onChange={(e) =>
                    setGivenAmount(parseFloat(e.target.value) || 0)
                  }
                  value={givenAmount}
                />
              </div>
              {givenAmount < totalPayable && (
                <p className="text-red-500 text-sm">
                  {languageHelper(
                    "Received amount cannot be less than the total amount."
                  )}
                </p>
              )}
              <div>
                <label className="block mb-1">
                  {languageHelper("Change Amount")}
                </label>
                <input
                  type="text"
                  className="border border-gray-300 bg-gray-200 cursor-not-allowed rounded p-2 w-full"
                  disabled
                  onChange={(e) => setChangeAmount(parseFloat(e.target.value))}
                  value={givenAmount - totalPayable}
                />
              </div>
              <div className="flex justify-end gap-4 mt-4">
                <button
                  className="text-white bg-d-cat-btn px-4 py-2 rounded-md"
                  type="button"
                  onClick={() => setShowDirectInvoiceFinalizeModal(false)}
                >
                  {languageHelper("Cancel")}
                </button>
                <button
                  className="text-white bg-d-cat-btn px-4 py-2 rounded-md"
                  disabled={loading}
                  type="submit"
                >
                  {languageHelper("Direct Invoice")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {alert && (
        <AlertModal alert={alert} handleCloseAlert={handleCloseAlert} />
      )}
      {openKotModal && (
        <KotModal
          orderDetails={orderDetails}
          setOpenKotModal={setOpenKotModal}
        />
      )}
      {invoiceData && (
        <div className="absolute left-[-9999px] top-0 opacity-0">
          <div ref={componentRef}>
            <InvoiceMain
              setting={setting}
              invoiceDetails={invoiceData}
              handlePrint={handleSilentPrint}
              outletData={outletData}
            />
          </div>
        </div>
      )}

      {/* Subscription Renewal Alert Modal */}
      <SubscriptionRenewalModal
        isOpen={showRenewalModal}
        onClose={handleCloseRenewalModal}
        renewalDate={renewalSetupData?.renewal_date}
        phoneNumber="0537565625"
      />
    </>
  );
}

export default POSMain;

function KotModal({ orderDetails, setOpenKotModal }) {
  const [selectedOrders, setSelectedOrders] = useState([]);
  const componentRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Invoice",
  });
  // Initialize selectedOrders with all items and their modifiers
  useEffect(() => {
    if (orderDetails?.order_details) {
      setSelectedOrders(
        orderDetails.order_details?.map((order) => ({
          ...order,
          selected_modifiers: order.modifiers ? [...order.modifiers] : [], // Ensure modifiers are initialized
        }))
      );
    }
  }, [orderDetails]);

  if (!orderDetails) return;
  // Toggle individual item selection
  const handleSelectItem = (order) => {
    setSelectedOrders((prevSelected) => {
      const isAlreadySelected = prevSelected.some(
        (selectedOrder) => selectedOrder?.food_menu_id === order.food_menu_id
      );
      if (isAlreadySelected) {
        return prevSelected.filter(
          (selectedOrder) => selectedOrder?.food_menu_id !== order.food_menu_id
        );
      } else {
        return [
          ...prevSelected,
          {
            ...order,
            selected_modifiers: order.modifiers ? [...order.modifiers] : [],
          },
        ];
      }
    });
  };

  // Toggle "Select All" checkbox
  const handleSelectAll = () => {
    if (selectedOrders.length === orderDetails.order_details?.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(
        orderDetails.order_details?.map((order) => ({
          ...order,
          selected_modifiers: order.modifiers ? [...order.modifiers] : [],
        }))
      );
    }
  };

  // Update quantity of a specific item in selectedOrders
  const updateOrderQuantity = (id, newQty) => {
    setSelectedOrders((prevSelected) =>
      prevSelected.map((order) =>
        order.food_menu_id === id ? { ...order, qty: newQty } : order
      )
    );
  };

  // Toggle modifier selection for an item
  const handleToggleModifier = (orderId, modifier) => {
    setSelectedOrders((prevSelected) =>
      prevSelected.map((order) => {
        if (order.food_menu_id === orderId) {
          const isModifierSelected = order.selected_modifiers?.some(
            (mod) => mod.name === modifier.name
          );
          const updatedModifiers = isModifierSelected
            ? order.selected_modifiers.filter(
              (mod) => mod.name !== modifier.name
            )
            : [...(order.selected_modifiers || []), modifier];
          return { ...order, selected_modifiers: updatedModifiers };
        }
        return order;
      })
    );
  };

  // Check if all items are selected
  const isAllSelected =
    selectedOrders.length === orderDetails.order_details?.length;

  return (
    <div
      className="h-screen w-full z-20 fixed top-0 left-0 bg-black bg-opacity-50 backdrop-blur-md grid place-items-center"
      onClick={() => setOpenKotModal(false)}
    >
      <div
        className="bg-white rounded-md max-w-xl w-full py-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center py-2">
          <h1 className="text-2xl font-bold">{languageHelper("KOT")}</h1>
          <p>
            {languageHelper("Order No")}: {orderDetails.sale_no}
          </p>
          <p>
            {languageHelper("Date")}: {orderDetails.date_time}
          </p>
          <p>
            {languageHelper("Customer")}:{" "}
            {orderDetails.is_guest === "no"
              ? orderDetails.customer?.name
              : orderDetails.guest?.name}
          </p>
          <p>
            {languageHelper("Table")}: {orderDetails.table?.name || "None"}
          </p>
          <p>
            {languageHelper("Waiter")}: None, {languageHelper("Order Type")}:{" "}
            {orderDetails.order_type.type}
          </p>
        </div>
        <table className="w-full">
          <thead className="bg-d-bg text-white text-lg">
            <tr>
              <th className="text-start px-2">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
              </th>
              <th className="text-start px-2">{languageHelper("Item")}</th>
              <th className="text-start px-2">{languageHelper("Qty")}</th>
              <th className="text-start px-2">{languageHelper("Add Ons")}</th>
            </tr>
          </thead>
          <tbody>
            {orderDetails.order_details?.map((order, index) => (
              <tr key={index}>
                <td className="px-2">
                  <input
                    type="checkbox"
                    checked={selectedOrders.some(
                      (selectedOrder) =>
                        selectedOrder?.food_menu_id === order.food_menu_id
                    )}
                    onChange={() => handleSelectItem(order)}
                  />
                </td>
                <td className="px-2">{order.name}</td>
                <td className="px-2">
                  <QuantityInput
                    item={order}
                    updateQuantity={updateOrderQuantity}
                  />
                </td>
                <td className="px-2">
                  <div className="flex flex-wrap gap-2">
                    {order.modifiers?.map((mod, index) => (
                      <span
                        key={index}
                        className={`border rounded text-sm px-2 py-1 cursor-pointer ${selectedOrders
                          .find(
                            (selectedOrder) =>
                              selectedOrder?.food_menu_id ===
                              order.food_menu_id
                          )
                          ?.selected_modifiers?.some(
                            (selectedMod) => selectedMod.name === mod.name
                          )
                          ? "bg-d-bg bg-opacity-50"
                          : ""
                          }`}
                        onClick={() =>
                          handleToggleModifier(order.food_menu_id, mod)
                        }
                      >
                        {mod.name}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex gap-4 justify-between px-4 mt-16">
          <button
            className="bg-d-cat-btn text-white text-lg font-medium text-center px-4 py-1 rounded"
            onClick={() => setOpenKotModal(false)}
          >
            {languageHelper("Cancel")}
          </button>
          <button
            className="bg-d-cat-btn text-white text-lg font-medium text-center px-4 py-1 rounded"
            onClick={() => {
              handlePrint();
              setOpenKotModal(false);
            }}
          >
            {languageHelper("Print KOT")}
          </button>
        </div>
      </div>
      <div className="hidden">
        <div ref={componentRef}>
          <KOTSingle orders={selectedOrders} data={orderDetails} />
        </div>
      </div>
    </div>
  );
}

function QuantityInput({ item, updateQuantity }) {
  // Initialize the state with the item's original quantity
  const [quantity, setQuantity] = useState(item.qty);
  // Function to increment quantity
  const incrementQty = () => {
    const newQty = quantity + 1;
    setQuantity(newQty);
    updateQuantity(item.food_menu_id, newQty);
  };

  // Function to decrement quantity
  const decrementQty = () => {
    // Ensure quantity does not go below the initial item quantity
    const newQty = quantity > item.qty ? quantity - 1 : item.qty;
    setQuantity(newQty);
    updateQuantity(item.food_menu_id, newQty);
  };

  // Handle manual input change
  const handleInputChange = (e) => {
    const newQty = parseFloat(e.target.value || 0);
    // Ensure the quantity is not less than the initial item quantity
    const validQty = newQty >= item.qty ? newQty : item.qty;
    setQuantity(validQty);
    updateQuantity(item.food_menu_id, validQty);
  };

  return (
    <span className="flex gap-x-1">
      {/* Decrement Button */}
      <button className="bg-d-bg p-1 rounded" onClick={decrementQty}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="16"
          width="14"
          viewBox="0 0 448 512"
          fill="white"
        >
          <path d="M432 256c0 17.7-14.3 32-32 32L48 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l352 0c17.7 0 32 14.3 32 32z" />
        </svg>
      </button>

      {/* Quantity Input */}
      <input
        className="w-8 border rounded text-center"
        type="text"
        value={quantity}
        onChange={handleInputChange}
      />

      {/* Increment Button */}
      <button className="bg-d-bg p-1 rounded" onClick={incrementQty}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="16"
          width="14"
          viewBox="0 0 448 512"
          fill="white"
        >
          <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z" />
        </svg>
      </button>
    </span>
  );
}

function AlertModal({ alert, handleCloseAlert }) {
  return (
    <div className="h-screen w-full z-20 fixed top-0 left-0 bg-black bg-opacity-50 backdrop-blur-md grid place-items-center">
      <div className="bg-white rounded-lg max-w-md w-full overflow-hidden p-4">
        <h2 className="text-[#595959] text-3xl font-bold text-center">
          {languageHelper("Alert")}
        </h2>
        <p className="text-[#595959] my-4 text-center">{alert}</p>
        <div className="w-full my-2">
          <button
            onClick={() => handleCloseAlert()}
            className="bg-d-cat-btn px-5 py-1 text-white text-lg font-bold rounded mx-auto block"
          >
            {languageHelper("OK")}
          </button>
        </div>
      </div>
    </div>
  );
}
